import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { Eyebrow, PrimaryButton, SecondaryButton, AirflowCurves } from "@/components/Brand";
import { MaskedLines } from "@/lib/motion";
import { SITE } from "@/lib/site";

export default function PageHero({
  eyebrow,
  titleLines,
  intro,
  ctaText,
  ctaTo = "/contact",
  secondaryPhone = false,
  image,
  imageAlt = "",
}) {
  return (
    <section className="relative overflow-hidden bg-white" data-testid="page-hero">
      <AirflowCurves className="pointer-events-none absolute -right-16 top-0 w-[480px] opacity-50" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 md:py-24 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <Eyebrow>{eyebrow}</Eyebrow>
          </motion.div>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            <MaskedLines lines={titleLines} delay={0.15} />
          </h1>
          <motion.p
            className="mt-6 max-w-xl text-base leading-relaxed text-mutedblue md:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {intro}
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            {ctaText && (
              <PrimaryButton to={ctaTo} testid="page-hero-cta">
                {ctaText}
              </PrimaryButton>
            )}
            {secondaryPhone && (
              <SecondaryButton href={SITE.phoneHref} testid="page-hero-phone">
                <Phone className="h-4 w-4" /> {SITE.phone}
              </SecondaryButton>
            )}
          </motion.div>
        </div>
        {image && (
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="overflow-hidden rounded-[20px] border border-cardline shadow-[0_24px_60px_rgba(5,59,117,0.12)]">
              <img
                src={image}
                alt={imageAlt}
                className="h-[310px] w-full object-cover md:h-[500px]"
                width="1200"
                height="950"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-2xl bg-ice md:h-32 md:w-32" style={{ zIndex: -1 }} aria-hidden="true" />
          </motion.div>
        )}
      </div>
    </section>
  );
}
